import axios from "axios"

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()

  // Set global axios defaults
  axios.defaults.withCredentials = true
  axios.defaults.withXSRFToken = true

  const axiosInstance = axios.create({
    baseURL: config.public.apiBase || "http://localhost:8000",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      "Content-Type": "application/json",
    },
    withCredentials: true, // Required for Sanctum
  })

  // Get the authentication store
  const authStore = useAuthStore()
  const tokenCookie = useCookie("auth_token") // Read token from cookie (fallback)

  // Request Interceptor
  axiosInstance.interceptors.request.use(
    (config) => {
      // Add Bearer Token only for API endpoints
      if (authStore.token) {
        config.headers.Authorization = `Bearer ${authStore.token}`
      }

      // Ensure API prefix for auth endpoints
      if (!config.url.startsWith("/api/") && !config.url.startsWith("/sanctum/")) {
        config.url = `/api${config.url}`
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  // Response Interceptor
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      // ✅ Skip retry logic if it's a login or logout request
      if (originalRequest.url?.includes("/login") || originalRequest.url?.includes("/logout")) {
        return Promise.reject(error)
      }
      if (originalRequest.url?.includes("/user")) {
        return Promise.reject(error)
      }

      // Prevent retry loops
      if (originalRequest._retry || originalRequest._forceLogout) {
        return Promise.reject(error)
      }

      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        originalRequest._retry = true

        try {
          const isStillLoggedIn = await authStore.checkAuth()
          if (isStillLoggedIn === true) {
            return axiosInstance(originalRequest)
          }

          originalRequest._forceLogout = true
          authStore.logout()
          navigateTo("/auth/login", { replace: true })

          return Promise.reject(error)
        } catch (refreshError) {
          originalRequest._forceLogout = true
          authStore.logout()
          navigateTo("/auth/login", { replace: true })

          return Promise.reject(refreshError)
        }
      }

      // Handle 419 CSRF token mismatch
      if (error.response?.status === 419 && !originalRequest._csrfRetried) {
        originalRequest._csrfRetried = true

        try {
          await axios.get(config.public.apiBase + "/sanctum/csrf-cookie")
          return axiosInstance(originalRequest)
        } catch (csrfError) {
          return Promise.reject(csrfError)
        }
      }

      console.error("API Error:", error.response?.data || error.message)
      return Promise.reject(error)
    }
  )

  // Provide Axios instance
  return {
    provide: {
      axios: axiosInstance,
    },
  }
})
